import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import { bombasColors } from 'Styles/colors';
import { convertColorToThemeHexCode } from 'Styles/colors.util';
import CTAPropTypes from '../../prop-types/cta';
import RichTextPropTypes from '../../prop-types/rich-text';
import { useBreakpoint } from '../../util/Breakpoint.context';
import CTABlock from '../CTABlock/CTABlock';
import List from '../List/List';

import * as Styled from './ContentBlock.styled';
import { ContentBlockFragment } from './ContentBlock.gql';
import ImageProps from '../../prop-types/image-props.pim';

const richTextOptions = {
  renderNode: {
    [BLOCKS.UL_LIST]: (_node, children) => (
      <List $listItems={React.Children.count(children)}>{children}</List>
    ),
    [BLOCKS.PARAGRAPH]: (_node, children) =>
      children[0] !== '' ? <p>{children}</p> : <></>
  }
};

/**
 * This component is a wrapper for the Module System Content Block and the DEPRECATED Content Block
 *
 * If there is an image present in props, the Module System Content Block is rendered.
 *
 * If there isn't an image present in props, the DEPRECATED Content Block is rendered. content_cards or html must be present to render DEPRECATED Content Block
 */
const ContentBlock = ({
  image,
  backgroundColor,
  customBackgroundColor,
  icon,
  content,
  ctas,
  imagePosition,
  imageStyle,
  layout,
  hasTopPadding
}) => {
  const { largest } = useBreakpoint();
  const [forceInset, setForceInset] = useState(false);
  const color =
    customBackgroundColor || convertColorToThemeHexCode(backgroundColor);
  const src = image?.file && image.file.url;
  const { height, width } = src ? image.file.details.image : {};
  const ratio = `${layout[layout.length - 2]}:${layout[layout.length - 1]}`;
  const StyledWrapper =
    imageStyle === 'BLEED' && !forceInset
      ? Styled.BleedWrapper
      : Styled.InsetWrapper;
  const imagePos = imagePosition.toLowerCase();
  let removePadding;

  if (ratio === '7:5') {
    if (imagePos === 'left') {
      removePadding = 'right';
    }
    if (imagePos === 'right') {
      removePadding = 'left';
    }
  }

  /**
   * When the screen is larger than the max width of the grid
   * we force content blocks to be inset regardless of how they were defined in the API
   */
  useEffect(() => {
    setForceInset(largest);
  }, [largest]);

  return src ? (
    <StyledWrapper
      $backgroundColor={color}
      $imageStyle={forceInset ? 'inset' : imageStyle.toLowerCase()}
      $imagePosition={imagePos}
      $layout={ratio}
      hasTopPadding={hasTopPadding}
    >
      <Styled.ImageBlock>
        <Styled.Image
          src={src.match(/https|http/) ? src : `https://${src}`}
          alt={image.description || ''}
          resolution={{
            small: width * 2
          }}
          crop={{
            height,
            width
          }}
        />
      </Styled.ImageBlock>

      <Styled.EditorialBlock
        $imagePosition={imagePos}
        $imageStyle={forceInset ? 'inset' : imageStyle.toLowerCase()}
        $layout={ratio}
        hasTopPadding={hasTopPadding}
        removePadding={removePadding}
      >
        {icon && icon.file.url && (
          <Styled.Icon src={icon.file.url} alt="icon" />
        )}
        {Object.keys(content).length > 0 && (
          <Styled.RichText
            $backgroundColor={color}
            $hasBottomMargin={ctas?.length}
          >
            {documentToReactComponents(content, richTextOptions)}
          </Styled.RichText>
        )}
        {ctas?.length > 0 ? <CTABlock ctas={ctas} color={color} /> : null}
      </Styled.EditorialBlock>
    </StyledWrapper>
  ) : null;
};

ContentBlock.fragment = ContentBlockFragment;

ContentBlock.propTypes = {
  /**
   * An image object
   */
  image: ImageProps,
  /**
   * A color name from the API
   *
   * Gets mapped to a color name in our code base to retrieve the hex code
   */
  backgroundColor: PropTypes.oneOf(bombasColors),
  /**
   * A hex code.
   *
   * Overrides backgroundColor if truthy
   */
  customBackgroundColor: PropTypes.string,
  /**
   * An icon object
   */
  icon: PropTypes.shape({
    file: PropTypes.shape({
      /**
       * An image URL
       */
      url: PropTypes.string.isRequired
    })
  }),
  /**
   * Rich text in a Contentful AST
   */
  content: PropTypes.shape(RichTextPropTypes),
  /**
   * An array of ctas
   */
  ctas: PropTypes.arrayOf(PropTypes.shape(CTAPropTypes)),
  /**
   * Toggles the image between the left and right sides
   */
  imagePosition: PropTypes.oneOf(['LEFT', 'RIGHT']),
  /**
   * Toggle between inset (image has border around it) and bleed (image goes to edge of screen)
   */
  imageStyle: PropTypes.oneOf(['INSET', 'BLEED']),
  /**
   * The image:editorial column spans
   */
  layout: PropTypes.oneOf(['RATIO84', 'RATIO75']),
  /**
   * Removes top padding from content block
   */
  hasTopPadding: PropTypes.bool
};

ContentBlock.defaultProps = {
  image: null,
  backgroundColor: 'grey20',
  customBackgroundColor: null,
  content: {},
  layout: 'RATIO75',
  imagePosition: 'LEFT',
  imageStyle: 'BLEED',
  ctas: [],
  icon: null,
  hasTopPadding: true
};

export default ContentBlock;
